import { useState } from 'react';
import { useApp } from '../../app/AppState';
import { ASSISTANT_API, type IssueBody, type IssueResult } from '../../assistant/providers/serverContract';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { displayName } from '../../domain/names';
import { useI18n } from '../../i18n';
import { uuid } from '../../lib/uuid';
import { useRepos } from '../../repositories';

type Kind = IssueBody['kind'];
const KINDS: Kind[] = ['fault', 'idea', 'question'];

/**
 * "Report a problem" — from the burger menu, for anyone in the family.
 *
 * It goes two places at once: the administrators' reports inbox in the app
 * (like a report made through the assistant), and an issue in the app's GitHub
 * repository, filed by the server so nobody needs a GitHub account. Where the
 * person was in the app is attached without asking. If GitHub is not connected
 * the report still reaches the administrators, and the thanks says only that.
 */
export function ReportIssueDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { me, session, adminMode, experience, online, pendingOps, reload } = useApp();
  const [kind, setKind] = useState<Kind>('fault');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<null | { issue?: number }>(null);
  const [error, setError] = useState(false);

  const close = () => {
    onClose();
    // Ready for the next one once the sheet is away.
    setTimeout(() => {
      setKind('fault');
      setTitle('');
      setDetail('');
      setDone(null);
      setError(false);
    }, 300);
  };

  const send = async () => {
    const cleanTitle = title.trim();
    if (!cleanTitle || sending) return;
    setSending(true);
    setError(false);
    const context = {
      path: location.pathname,
      lang,
      mode: experience,
      role: session?.role ?? 'member',
      adminMode,
      online,
      pending: pendingOps,
      userAgent: navigator.userAgent.slice(0, 200),
      viewport: `${window.innerWidth}×${window.innerHeight}`,
    };
    const saved = await repos.feedback.send({ kind, title: cleanTitle, detail: detail.trim() || undefined, context, idempotencyKey: uuid() });
    let issue: number | undefined;
    try {
      const body: IssueBody = { kind, title: cleanTitle, detail: detail.trim() || undefined, reporter: me ? displayName(me, 'en') : undefined, context };
      const res = await fetch(`${ASSISTANT_API}/issue`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
      if (res.ok) issue = ((await res.json()) as IssueResult).number;
    } catch {
      /* the administrators still have it */
    }
    setSending(false);
    if (!saved.ok && issue === undefined) {
      setError(true);
      return;
    }
    if (saved.ok) reload();
    setDone({ issue });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t('issue.title')}
      actions={
        done ? (
          <button type="button" className="btn btn-primary" onClick={close}>
            <Icon name="check" />
            <span>{t('issue.close')}</span>
          </button>
        ) : (
          <button type="button" className="btn btn-primary" disabled={!title.trim() || sending} onClick={() => void send()} data-assist="issue.send">
            <Icon name="share" />
            <span>{sending ? t('issue.sending') : t('issue.send')}</span>
          </button>
        )
      }
    >
      {done ? (
        <p role="status">{done.issue !== undefined ? t('issue.thanksGithub', { n: done.issue }) : t('issue.thanks')}</p>
      ) : (
        <>
          <div className="field">
            <span className="field-label">{t('issue.kind')}</span>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {KINDS.map((k) => (
                <button key={k} type="button" className="chip chip-btn" aria-pressed={kind === k} onClick={() => setKind(k)}>
                  {t(`issue.kind.${k}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="issue-title">{t('issue.what')}</label>
            <input id="issue-title" className="input" dir="auto" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(`issue.what.placeholder.${kind}`)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="issue-detail">{t('issue.detail')}</label>
            <textarea id="issue-detail" className="input" rows={4} dir="auto" maxLength={4000} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={t('issue.detail.placeholder')} />
            <p className="help">{t('issue.help')}</p>
          </div>
          {error && <p className="help" role="alert">{t('issue.error')}</p>}
        </>
      )}
    </Dialog>
  );
}
